import { supabase } from '../supabaseClient';
import type {
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  getProgramScopedRowsQueryData,
  setProgramFixedRowsQueryData,
  setProgramProgressCellsQueryData,
  setProgramScopedContentRowsQueryData,
  setProgramScopedRowsQueryData,
} from '../query/cacheUpdates';
import {
  clearAnnualProgressScopedTermCache,
  loadAnnualProgressScopedTermCache,
  mergeAnnualProgressScopedTermCacheRows,
  removeIdsFromAnnualProgressScopedTermRows,
  replaceScopeRowsInAnnualProgressScopedTermRows,
  saveAnnualProgressScopedTermCache,
} from './annualProgressScopedTermCache';
import {
  loadProgramFixedContentRowsCache,
  mergeProgramFixedContentRowsCacheRows,
  saveProgramFixedContentRowsCache,
} from './programFixedContentRowsCache';
import {
  loadProgramProgressCellsCache,
  mergeProgramProgressCellsCacheRows,
  saveProgramProgressCellsCache,
} from './programProgressCellsCache';
import {
  loadProgramScopedContentRowsCache,
  mergeProgramScopedContentRowsCacheRows,
  saveProgramScopedContentRowsCache,
} from './programScopedContentRowsCache';
import type { ProgramLearningTextOwnerPatchKind } from './programLearningTextPatchMerge';

/**
 * 편제/프로젝트 스코프 행 변경을 연간진도표가 읽는 학년·학기 캐시(LS + RQ)에도 반영한다.
 * (스코프별 캐시만 갱신하면 연간진도표 인디케이터가 옛 learning_text를 본다.)
 */
export function mergeScopedRowsIntoAnnualGradeTermCaches(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  updatedRows: readonly ProgramScopedContentRow[]
): void {
  if (!updatedRows.length) return;
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  if (!sid || !gid) return;

  const ls = loadAnnualProgressScopedTermCache(sid, academicYear, termNumber, gid);
  if (ls.found) {
    const merged = mergeAnnualProgressScopedTermCacheRows(ls.rows, [...updatedRows]);
    saveAnnualProgressScopedTermCache(sid, academicYear, termNumber, gid, merged);
    setProgramScopedRowsQueryData(sid, academicYear, termNumber, gid, merged);
    return;
  }

  const rq = getProgramScopedRowsQueryData(sid, academicYear, termNumber, gid);
  if (rq) {
    setProgramScopedRowsQueryData(
      sid,
      academicYear,
      termNumber,
      gid,
      mergeAnnualProgressScopedTermCacheRows(rq, [...updatedRows])
    );
  }
}

/**
 * 한 스코프 저장 결과(그 스코프 전체 행)로 연간진도표 학년·학기 캐시(LS + RQ)의 그 스코프 행을 통째 교체한다.
 * LS가 없어도(다른 저장이 LS를 비운 뒤) 화면에 떠 있는 RQ는 반드시 맞춘다 — 안 맞추면 staleTime 무한이라
 * 연간진도표가 지워진 옛 행(옛 학습내용)을 계속 보여 준다.
 */
export function replaceScopeRowsInAnnualGradeTermCaches(
  params: {
    workspaceId: string;
    academicYear: number;
    termNumber: 1 | 2;
    gradeId: string;
    scopeType: string;
    scopeKey: string;
  },
  scopeRows: readonly ProgramScopedContentRow[]
): void {
  const sid = params.workspaceId.trim();
  const gid = params.gradeId.trim();
  if (!sid || !gid || !params.scopeKey) return;
  const scope = { termNumber: params.termNumber, scopeType: params.scopeType, scopeKey: params.scopeKey };
  const ls = loadAnnualProgressScopedTermCache(sid, params.academicYear, params.termNumber, gid);
  if (ls.found) {
    const next = replaceScopeRowsInAnnualProgressScopedTermRows(ls.rows, scope, scopeRows);
    saveAnnualProgressScopedTermCache(sid, params.academicYear, params.termNumber, gid, next);
    setProgramScopedRowsQueryData(sid, params.academicYear, params.termNumber, gid, next);
    return;
  }
  clearAnnualProgressScopedTermCache(sid, params.academicYear, params.termNumber, gid);
  const rq = getProgramScopedRowsQueryData(sid, params.academicYear, params.termNumber, gid);
  if (rq) {
    setProgramScopedRowsQueryData(
      sid,
      params.academicYear,
      params.termNumber,
      gid,
      replaceScopeRowsInAnnualProgressScopedTermRows(rq, scope, scopeRows)
    );
  }
}

/** 삭제된 행 id를 연간진도표 학년·학기 캐시(LS + RQ)에서 뺀다 */
export function removeScopedRowIdsFromAnnualGradeTermCaches(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  ids: ReadonlySet<string>
): void {
  const sid = workspaceId.trim();
  const gid = gradeId.trim();
  if (!sid || !gid || !ids.size) return;
  const ls = loadAnnualProgressScopedTermCache(sid, academicYear, termNumber, gid);
  if (ls.found) {
    saveAnnualProgressScopedTermCache(
      sid,
      academicYear,
      termNumber,
      gid,
      removeIdsFromAnnualProgressScopedTermRows(ls.rows, ids)
    );
  } else {
    clearAnnualProgressScopedTermCache(sid, academicYear, termNumber, gid);
  }
  const rq = getProgramScopedRowsQueryData(sid, academicYear, termNumber, gid);
  if (rq) {
    setProgramScopedRowsQueryData(sid, academicYear, termNumber, gid, removeIdsFromAnnualProgressScopedTermRows(rq, ids));
  }
}

export type ProgramLearningOwnerRef = {
  kind: ProgramLearningTextOwnerPatchKind;
  id: string;
};

function ownerKey(o: ProgramLearningOwnerRef): string {
  return `${o.kind}:${o.id.trim()}`;
}

export function dedupeProgramLearningOwnerRefs(
  owners: readonly ProgramLearningOwnerRef[]
): ProgramLearningOwnerRef[] {
  const seen = new Set<string>();
  const out: ProgramLearningOwnerRef[] = [];
  owners.forEach((o) => {
    const id = (o.id ?? '').trim();
    if (!id) return;
    if (o.kind !== 'program_cell' && o.kind !== 'fixed_row' && o.kind !== 'scoped_row') return;
    const k = ownerKey({ kind: o.kind, id });
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ kind: o.kind, id });
  });
  return out;
}

async function fetchProgramProgressCellById(
  workspaceId: string,
  id: string
): Promise<ProgramProgressCell | null> {
  const { data, error } = await supabase
    .from('program_progress_cells')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramProgressCell | null) ?? null;
}

async function fetchProgramFixedRowById(
  workspaceId: string,
  id: string
): Promise<ProgramFixedContentRow | null> {
  const { data, error } = await supabase
    .from('program_fixed_content_rows')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramFixedContentRow | null) ?? null;
}

async function fetchProgramScopedRowById(
  workspaceId: string,
  id: string
): Promise<ProgramScopedContentRow | null> {
  const { data, error } = await supabase
    .from('program_scoped_content_rows')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ProgramScopedContentRow | null) ?? null;
}

/**
 * 슬라이드·활동지 flush 후: 바뀐 표 행만 SELECT 해서 LS/RQ 캐시에 끼워 넣는다.
 * @returns false면 캐시 없음·조회 실패 등으로 전량 재조회(full)가 필요
 */
export async function mergeFlushedLearningOwnerRowsIntoCaches(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  owners: readonly ProgramLearningOwnerRef[];
}): Promise<boolean> {
  const workspaceId = params.workspaceId.trim();
  const gradeId = params.gradeId.trim();
  const owners = dedupeProgramLearningOwnerRefs(params.owners);
  if (!workspaceId || !gradeId || !owners.length) return true;

  try {
    for (const owner of owners) {
      if (owner.kind === 'program_cell') {
        const row = await fetchProgramProgressCellById(workspaceId, owner.id);
        if (!row) return false;
        if (row.grade_id !== gradeId || row.academic_year !== params.academicYear || row.term_number !== params.termNumber) {
          return false;
        }
        const cached = loadProgramProgressCellsCache(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId
        );
        if (!cached.found) return false;
        const merged = mergeProgramProgressCellsCacheRows(cached.rows, [row]);
        saveProgramProgressCellsCache(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId,
          merged,
          cached.coverage
        );
        setProgramProgressCellsQueryData(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId,
          merged
        );
        continue;
      }

      if (owner.kind === 'fixed_row') {
        const row = await fetchProgramFixedRowById(workspaceId, owner.id);
        if (!row) return false;
        if (row.grade_id !== gradeId || row.academic_year !== params.academicYear || row.term_number !== params.termNumber) {
          return false;
        }
        const cached = loadProgramFixedContentRowsCache(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId
        );
        if (!cached.found) return false;
        const merged = mergeProgramFixedContentRowsCacheRows(cached.rows, [row]);
        saveProgramFixedContentRowsCache(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId,
          merged
        );
        setProgramFixedRowsQueryData(
          workspaceId,
          params.academicYear,
          params.termNumber,
          gradeId,
          merged
        );
        continue;
      }

      const row = await fetchProgramScopedRowById(workspaceId, owner.id);
      if (!row) return false;
      if (row.grade_id !== gradeId || row.academic_year !== params.academicYear || row.term_number !== params.termNumber) {
        return false;
      }
      const scopeType = row.scope_type;
      const scopeKey = (row.scope_key ?? '').trim();
      if (!scopeType || !scopeKey) return false;
      const cached = loadProgramScopedContentRowsCache(
        workspaceId,
        params.academicYear,
        params.termNumber,
        gradeId,
        scopeType,
        scopeKey
      );
      if (!cached.found) return false;
      const merged = mergeProgramScopedContentRowsCacheRows(cached.rows, [row]);
      saveProgramScopedContentRowsCache(
        workspaceId,
        params.academicYear,
        params.termNumber,
        gradeId,
        scopeType,
        scopeKey,
        merged
      );
      setProgramScopedContentRowsQueryData(
        workspaceId,
        params.academicYear,
        params.termNumber,
        gradeId,
        scopeType,
        scopeKey,
        merged
      );
      mergeScopedRowsIntoAnnualGradeTermCaches(
        workspaceId,
        params.academicYear,
        params.termNumber,
        gradeId,
        [row]
      );
    }
    return true;
  } catch {
    return false;
  }
}
